import { useAuth } from '@/contexts/AuthContext';
import type { PermitCalculationResponse } from '@/types/api.types';

interface FeeResultsDisplayProps {
  result: PermitCalculationResponse;
}

export default function FeeResultsDisplay({ result }: FeeResultsDisplayProps) {
  const { isAdmin } = useAuth();
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatConfidence = (score: number) => {
    return `${(score * 100).toFixed(0)}%`;
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Low Confidence Warning Banner */}
      {result.credit_cost === 0 && result.low_confidence_free && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">Free Search - Very Low Confidence</h3>
              <p className="text-sm text-amber-800">
                Our confidence in the following calculation is below our standard, so this search is
                free (0 credits charged). We have logged this scenario for improvement.
              </p>
            </div>
          </div>
        </div>
      )}
      {result.credit_cost === 0.5 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 mb-1">Half Credit Search - Low Confidence</h3>
              <p className="text-sm text-yellow-800">
                Our confidence in the following calculation is moderate, so only 0.5 credits were charged.
                We have logged this scenario for improvement.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">✓</span>
          <div className="flex-1">
            <h3 className="font-semibold text-green-900 mb-1">Permit Fee Calculated</h3>
            <p className="text-sm text-green-800">
              Fee calculation completed for {result.municipality.name}, {result.municipality.state}
            </p>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {result.warnings && result.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900 mb-2">Warnings</h4>
              <ul className="list-disc list-inside space-y-1">
                {result.warnings.map((warning, idx) => (
                  <li key={idx} className="text-sm text-yellow-800">
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Municipality Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Municipality Information</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Municipality:</span>
            <span className="font-medium">
              {result.municipality.name}, {result.municipality.state}
            </span>
          </div>
          {result.municipality.website_url && (
            <div className="flex justify-between">
              <span className="text-gray-600">Website:</span>
              <a
                href={result.municipality.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
              >
                Visit Website →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Breakdown</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Base Fee:</span>
            <span className="font-medium">{formatCurrency(result.fee_breakdown.base_fee)}</span>
          </div>
          {result.fee_breakdown.state_surcharge > 0 && (
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-600">State Surcharge:</span>
              <span className="font-medium">
                {formatCurrency(result.fee_breakdown.state_surcharge)}
              </span>
            </div>
          )}
          {result.fee_breakdown.processing_fees > 0 && (
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-600">Processing Fees:</span>
              <span className="font-medium">
                {formatCurrency(result.fee_breakdown.processing_fees)}
              </span>
            </div>
          )}
          {result.fee_breakdown.administrative_overhead > 0 && result.admin_fee_breakdown && (
            <div className="py-2 border-b border-gray-200">
              <div className="flex justify-between">
                <span className="text-gray-600">Admin Overhead:</span>
                <span className="font-medium">
                  {formatCurrency(result.fee_breakdown.administrative_overhead)}
                </span>
              </div>
              <div className="mt-1 ml-4 space-y-0.5 text-xs text-gray-500">
                {result.admin_fee_breakdown.permit_buddy_cost > 0 && (
                  <div className="flex justify-between">
                    <span>Permit Buddy cost</span>
                    <span>{formatCurrency(result.admin_fee_breakdown.permit_buddy_cost)}</span>
                  </div>
                )}
                {result.admin_fee_breakdown.base_processing_fee > 0 && (
                  <div className="flex justify-between">
                    <span>Base processing fee</span>
                    <span>{formatCurrency(result.admin_fee_breakdown.base_processing_fee)}</span>
                  </div>
                )}
                {result.admin_fee_breakdown.per_inspection_fee_total > 0 && (
                  <div className="flex justify-between">
                    <span>Inspection coordination</span>
                    <span>
                      {formatCurrency(result.admin_fee_breakdown.per_inspection_fee_total)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-between py-3 bg-indigo-50 px-3 rounded-md">
            <span className="font-semibold text-gray-900">Total Fee:</span>
            <span className="font-bold text-xl text-indigo-600">
              {formatCurrency(result.fee_breakdown.total)}
            </span>
          </div>
        </div>
      </div>

      {/* Permit Link */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Permit Link</h3>
        {result.municipality.website_url ? (
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Apply for your permit:</span>
            <a
              href={result.municipality.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
            >
              {result.municipality.website_url} →
            </a>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">
            Our apologies, we were unable to find the permit link for this search.
          </p>
        )}
      </div>

      {/* Calculation Steps */}
      {result.calculation_steps && result.calculation_steps.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Calculation Steps</h3>
          <div className="space-y-4">
            {result.calculation_steps.map((step) => (
              <div key={step.step} className="border-l-4 border-indigo-500 pl-4 py-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    Step {step.step}: {step.method}
                  </span>
                  <span className="text-sm font-semibold text-indigo-600">
                    {formatCurrency(step.amount)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">{step.description}</p>
                {step.calculation && (
                  <p className="text-xs text-gray-500 font-mono bg-gray-50 p-2 rounded">
                    {step.calculation}
                  </p>
                )}
                {step.reasoning && (
                  <p className="text-xs text-gray-600 italic mt-1">{step.reasoning}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata - Admin Only */}
      {isAdmin && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Calculation Metadata</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-gray-600 block">Method:</span>
              <span className="font-medium text-gray-900">{result.calculation_method}</span>
            </div>
            <div>
              <span className="text-gray-600 block">Extraction:</span>
              <span className="font-medium text-gray-900">{result.extraction_method}</span>
            </div>
            <div>
              <span className="text-gray-600 block">Fee Confidence:</span>
              <span className={`font-medium ${getConfidenceColor(result.confidence_score)}`}>
                {formatConfidence(result.confidence_score)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 block">Processing Time:</span>
              <span className="font-medium text-gray-900">
                {result.processing_time.toFixed(2)}s
              </span>
            </div>
          </div>
          {result.override_applied && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded">
              ⚠️ Fee override was applied based on special rules
            </div>
          )}
          {(result.credit_cost !== undefined && result.credit_cost !== null) ? (
            <div className="mt-3 text-xs text-gray-600">
              Credits Charged: {result.credit_cost} (Transaction ID:{' '}
              {result.transaction_id})
            </div>
          ) : result.search_cost ? (
            <div className="mt-3 text-xs text-gray-600">
              Search Cost: {formatCurrency(result.search_cost)} (Transaction ID:{' '}
              {result.transaction_id})
            </div>
          ) : null}
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-xl">ℹ️</span>
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Important Note</h4>
            <p className="text-sm text-blue-800">
              These fees are estimates based on available municipal data. Always verify with the
              municipality's official permit office before submitting your application. Fee
              structures may change without notice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
